import React, { useState, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import axios from 'axios';
import ChartContainer from '../vedic/results/ChartContainer';
import ScoreRing from '../vedic/results/ScoreRing';
import KootaGrid from '../vedic/results/KootaGrid';
import LoadingState from '../vedic/feedback/LoadingState';
import { freeKundliService } from '../../services/api';
import MatchingForm from '../vedic/forms/MatchingForm';
import { to24Hour } from '../vedic/forms/TimeInput';

/* ─── Animations ─── */
const fadeIn = keyframes`from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}`;
/* ─── Styled Components ─── */
const PageContainer = styled.div`max-width:1200px;margin:40px auto;padding:0 20px;`;
const PageTitle = styled.h1`font-size:2rem;text-align:center;margin-bottom:5px;color:${p => p.theme.colors.text.primary};`;
const PageSubtitle = styled.p`text-align:center;color:${p => p.theme.colors.text.secondary};margin-bottom:30px;font-size:0.95rem;`;

const Card = styled.div`
  background:${p => p.theme.colors.surface};border-radius: ${p => p.theme.radius.md}px;box-shadow: ${p => p.theme.shadow.card};padding:30px;
  animation:${fadeIn} 0.3s ease;
`;

const Select = styled.select`
  width:100%;padding:10px 12px;border:1px solid ${p => p.theme.colors.border};border-radius:6px;font-size:0.95rem;
  &:focus{outline:none;border-color:${p => p.theme.colors.primary}}
`;

const VerdictBanner = styled.div`
  text-align:center;padding:16px;border-radius: ${p => p.theme.radius.md}px;margin:16px 0;font-size:1.1rem;font-weight:600;
  background:${p => p.good ? '#e8f5e9' : '#fff3e0'};
  color:${p => p.good ? '#2e7d32' : '#e65100'};
`;

const BackButton = styled.button`
  background:none;border:1px solid ${p => p.theme.colors.border};border-radius:6px;padding:8px 20px;
  font-size:0.85rem;cursor:pointer;color:${p => p.theme.colors.text.secondary};margin-bottom:20px;
  &:hover{border-color:${p => p.theme.colors.primary};color:${p => p.theme.colors.primary}}
`;

const kootaInfo = [
  { key: 'dinam', name: 'Dinam', ta: 'தினம்', desc: 'Health and longevity of the couple after marriage' },
  { key: 'ganam', name: 'Ganam', ta: 'கணம்', desc: 'Temperament compatibility - Deva, Manushya, or Rakshasa' },
  { key: 'mahendra', name: 'Mahendra', ta: 'மகேந்திரம்', desc: 'Well-being and prosperity of future children' },
  { key: 'stree_deergha', name: 'Stree Deergha', ta: 'ஸ்திரீ தீர்க்கம்', desc: 'Longevity and well-being of the bride' },
  { key: 'yoni', name: 'Yoni', ta: 'யோனி', desc: 'Physical and intimate compatibility between partners' },
  { key: 'rasi', name: 'Rasi', ta: 'ராசி', desc: 'Emotional compatibility and family welfare' },
  { key: 'rajju', name: 'Rajju', ta: 'ரஜ்ஜு', desc: 'Longevity of marriage - most critical koota' },
  { key: 'vedha', name: 'Vedha', ta: 'வேதை', desc: 'Absence of afflictions between nakshatras' },
  { key: 'vasya', name: 'Vasya', ta: 'வசியம்', desc: 'Mutual attraction and magnetic compatibility' },
  { key: 'nadi', name: 'Nadi', ta: 'நாடி', desc: 'Physiological compatibility and progeny health' },
];

const langLabels = { en: 'English', ta: 'Tamil' };

/* ─── Component ─── */
const TenPorutham = () => {
  const [lang, setLang] = useState('en');
  const [person1Values, setPerson1Values] = useState({
    dob: '', hour: '', minute: '', period: 'AM', timeFormat: '12h',
    place: '', latitude: null, longitude: null, timezone: 'Asia/Kolkata',
  });
  const [person2Values, setPerson2Values] = useState({
    dob: '', hour: '', minute: '', period: 'AM', timeFormat: '12h',
    place: '', latitude: null, longitude: null, timezone: 'Asia/Kolkata',
  });
  const [ayanamsa] = useState('lahiri');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [brideChart, setBrideChart] = useState(null);
  const [groomChart, setGroomChart] = useState(null);

  const parseDob = (dob) => {
    const parts = dob.split('-');
    return { year: parseInt(parts[0]), month: parseInt(parts[1]), day: parseInt(parts[2]) };
  };

  const handleSubmit = useCallback(async (brideVals, groomVals) => {
    if (!brideVals.latitude || !brideVals.longitude) {
      setError('Please select girl\'s place from suggestions.');
      return;
    }
    if (!groomVals.latitude || !groomVals.longitude) {
      setError('Please select boy\'s place from suggestions.');
      return;
    }
    if (!brideVals.dob || !groomVals.dob) {
      setError('Please enter date of birth for both girl and boy.');
      return;
    }
    setError('');
    setLoading(true);
    const timezone = brideVals.timezone || 'Asia/Kolkata';
    try {
      const brideData = {
        ...parseDob(brideVals.dob),
        hour: to24Hour(parseInt(brideVals.hour || '0'), brideVals.period || 'AM', brideVals.timeFormat || '12h'),
        minute: parseInt(brideVals.minute || '0'),
        latitude: brideVals.latitude,
        longitude: brideVals.longitude,
      };
      const groomData = {
        ...parseDob(groomVals.dob),
        hour: to24Hour(parseInt(groomVals.hour || '0'), groomVals.period || 'AM', groomVals.timeFormat || '12h'),
        minute: parseInt(groomVals.minute || '0'),
        latitude: groomVals.latitude,
        longitude: groomVals.longitude,
      };
      const res = await axios.post('/vedic/v1/matching/porutham', {
        bride: brideData,
        groom: groomData,
        ayanamsa,
        timezone,
        lang,
      });
      setResult(res.data);
      // Fetch charts for bride and groom in parallel (best-effort)
      const makeChartPayload = (personData) => ({
        name: personData.name || 'Chart', gender: personData.gender || 'male',
        year: personData.year, month: personData.month, day: personData.day,
        hour: personData.hour, minute: personData.minute,
        place: '', latitude: personData.latitude, longitude: personData.longitude,
        timezone, ayanamsa,
      });
      Promise.all([
        freeKundliService.generateChart(makeChartPayload(brideData)).catch(() => null),
        freeKundliService.generateChart(makeChartPayload(groomData)).catch(() => null),
      ]).then(([brideRes, groomRes]) => {
        if (brideRes?.data?.chart) setBrideChart(brideRes.data.chart);
        if (groomRes?.data?.chart) setGroomChart(groomRes.data.chart);
      });
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to compute porutham');
    } finally {
      setLoading(false);
    }
  }, [ayanamsa, lang]);

  const totalScore = result?.total_score ?? result?.score ?? 0;
  const maxScore = result?.max_score ?? 10;
  const pct = maxScore > 0 ? Math.round((totalScore / maxScore) * 100) : 0;
  const scoreColor = pct >= 70 ? '#4caf50' : pct >= 40 ? '#ff9800' : '#f44336';
  const verdict = result?.verdict || result?.summary || '';
  const kootas = result?.kootas || result?.porutham || [];

  // Map API response kootas to our koota info
  const getKootaResults = () => {
    if (kootas.length > 0) return kootas;
    // Try to build from result fields
    return kootaInfo.map(ki => {
      const match = result?.[ki.key] || result?.kootas?.[ki.key];
      if (match) {
        return {
          name: ki.name,
          name_tamil: ki.ta,
          pass: match.pass ?? match.matched ?? match.result === 'pass',
          description: match.description || match.detail || ki.desc,
          ...match,
        };
      }
      return null;
    }).filter(Boolean);
  };

  return (
    <PageContainer>
      <PageTitle>{lang === 'ta' ? 'பத்து பொருத்தம்' : 'Ten Porutham (Koota Matching)'}</PageTitle>
      <PageSubtitle>{lang === 'ta' ? 'திருமணப் பொருத்தம் - 10 கூட்டங்கள்' : 'Marriage Compatibility - 10 Koota Analysis'}</PageSubtitle>

      <div style={{ textAlign: 'right', marginBottom: 10 }}>
        <Select value={lang} onChange={e => setLang(e.target.value)} style={{ width: 'auto', padding: '6px 10px' }}>
          {Object.entries(langLabels).map(([code, label]) => (
            <option key={code} value={code}>{label}</option>
          ))}
        </Select>
      </div>

      <LoadingState loading={loading} variant="score" />

      {!result ? (
        <MatchingForm
          person1Label={lang === 'ta' ? 'பெண் (Girl)' : 'Girl'}
          person2Label={lang === 'ta' ? 'ஆண் (Boy)' : 'Boy'}
          person1Color="#e91e63"
          person2Color="#1565c0"
          fields={['dob', 'time', 'place']}
          person1Values={person1Values}
          person2Values={person2Values}
          onPerson1Change={setPerson1Values}
          onPerson2Change={setPerson2Values}
          onSubmit={handleSubmit}
          submitLabel={loading ? 'Computing...' : (lang === 'ta' ? 'பொருத்தம் காண்' : 'Check Porutham')}
          loading={loading}
          error={error}
          pageName="tenPorutham"
          lang={lang}
        />
      ) : (
        <>
          <BackButton onClick={() => { setResult(null); setBrideChart(null); setGroomChart(null); }}>
            {lang === 'ta' ? 'புதிய ஒப்பீடு' : 'New Match'}
          </BackButton>

          {/* Birth Charts */}
          {(brideChart || groomChart) && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
              {brideChart && (
                <Card style={{ padding: 16 }}>
                  <ChartContainer chartData={brideChart} title={lang === 'ta' ? 'பெண்' : 'Girl'} />
                </Card>
              )}
              {groomChart && (
                <Card style={{ padding: 16 }}>
                  <ChartContainer chartData={groomChart} title={lang === 'ta' ? 'ஆண்' : 'Boy'} />
                </Card>
              )}
            </div>
          )}

          {/* Score */}
          <Card style={{ marginBottom: 20 }}>
            <ScoreRing value={totalScore} max={maxScore} color={scoreColor}
              label={lang === 'ta' ? 'பொருத்தம்' : 'Match'} size={180} />

            {verdict && (
              <VerdictBanner good={pct >= 60}>
                {verdict}
              </VerdictBanner>
            )}
          </Card>

          {/* Koota Details */}
          <KootaGrid
            expandable
            lang={lang}
            items={getKootaResults().map((koota, i) => {
              const info = kootaInfo.find(ki =>
                ki.key === (koota.key || koota.name?.toLowerCase().replace(/\s+/g, '_')) ||
                ki.name.toLowerCase() === (koota.name || '').toLowerCase()
              ) || kootaInfo[i] || {};
              const pass = koota.pass ?? koota.matched ?? false;
              return {
                name: koota.name || info.name || `Koota ${i + 1}`,
                nameTa: koota.name_tamil || info.ta,
                score: koota.score ?? (pass ? 1 : 0),
                maxScore: koota.max_points ?? 1,
                pass,
                color: koota.color,
                description: koota.description || koota.detail || info.desc || '',
              };
            })}
          />

          <div style={{ textAlign: 'center', marginTop: 24 }}>
            <BackButton onClick={() => { setResult(null); setBrideChart(null); setGroomChart(null); }} style={{ display: 'inline-block' }}>
              {lang === 'ta' ? 'புதிய ஒப்பீடு' : 'Check Another Match'}
            </BackButton>
          </div>
        </>
      )}
    </PageContainer>
  );
};

export default TenPorutham;
